/**
 * PagePreviewRenderer - Live preview renderer for Page Builder
 * 
 * Renders actual page blocks from draft configuration with click-to-select functionality.
 * Supports both homepage and landing page block types.
 * 
 * Features:
 * - Renders real components (not placeholders)
 * - Click-to-select blocks to edit in settings panel
 * - Respects editor mode (Element vs Section)
 * - Disabled interactivity (links don't navigate, forms don't submit)
 * - Responsive to viewport toggles
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import BlockWrapper from './BlockWrapper';
import { EditorMode } from './EditorModeToggle';
import { API_URL } from '../../config/api';

// Import homepage blocks
import {
  HeroCarouselBlock,
  TrendingBlock,
  NewReleasesBlock,
  FeaturedCreatorsBlock,
  FeaturedArtworksBlock,
  CTABannerBlock,
} from '../homepage-blocks';

const API = API_URL;

// Map block types to components and their data fetching requirements
const HOMEPAGE_BLOCK_CONFIG = {
  homepage_hero_carousel: {
    component: HeroCarouselBlock,
    dataEndpoint: '/homepage/hero',
    dataKey: 'slides', // Extract slides from response
    label: 'Hero Carousel'
  },
  homepage_trending: {
    component: TrendingBlock,
    // Special case - needs both trending and popular
    multipleEndpoints: [
      { endpoint: '/homepage/trending', key: 'trending', params: { limit: 10 } },
      { endpoint: '/homepage/popular', key: 'popular', params: { limit: 10 } }
    ],
    label: 'Trending Series'
  },
  homepage_new_releases: {
    component: NewReleasesBlock,
    dataEndpoint: '/homepage/new-releases',
    dataParams: { limit: 10 },
    dataKey: null,
    label: 'New Releases'
  },
  homepage_featured_series: {
    component: TrendingBlock, // Reuse trending component for featured
    dataEndpoint: '/homepage/carousel',
    dataKey: null, // Returns array directly
    transformToTrending: true, // Transform to trending format {trending: data, popular: []}
    label: 'Featured Series'
  },
  homepage_featured_creators: {
    component: FeaturedCreatorsBlock,
    dataEndpoint: '/featured-creators',
    dataKey: 'featured_creators',
    label: 'Featured Creators'
  },
  homepage_featured_artworks: {
    component: FeaturedArtworksBlock,
    dataEndpoint: '/featured-artworks',
    dataParams: { limit: 8 },
    dataKey: 'featured_artworks',
    label: 'Featured Artworks'
  },
  homepage_cta_banner: {
    component: CTABannerBlock,
    dataEndpoint: null, // No data fetch needed
    label: 'CTA Banner'
  }
};

const HomepageBlockRenderer = ({ block, blockData, isSelected, onSelect, editorMode, onHoverChange, onDuplicate, onDelete }) => {
  const config = HOMEPAGE_BLOCK_CONFIG[block.type];
  
  if (!config) {
    return (
      <BlockWrapper
        blockId={block.id}
        blockLabel={block.type}
        settingsKey={block.settings_key}
        isSelected={isSelected}
        onSelect={onSelect}
        editorMode={editorMode}
        onHoverChange={onHoverChange}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      >
        <div className="p-8 bg-zinc-900 border border-zinc-700 rounded-lg text-center my-4 mx-4">
          <p className="text-zinc-400">Unknown block type: {block.type}</p>
        </div>
      </BlockWrapper>
    );
  }

  const Component = config.component;
  const label = config.label;
  
  // Merge default props with block props
  const mergedProps = {
    ...block.props
  };
  
  // For CTA Banner, pass props directly as settings
  if (block.type === 'homepage_cta_banner') {
    const ctaSettings = {
      title: mergedProps.title || "Start Your Creative Journey",
      subtitle: mergedProps.subtitle || "Join thousands of creators sharing their stories",
      button_text: mergedProps.ctaLabel || "Create Account",
      button_link: mergedProps.ctaDestination || "/auth?mode=signup"
    };
    return (
      <BlockWrapper
        blockId={block.id}
        blockLabel={label}
        settingsKey={block.settings_key}
        isSelected={isSelected}
        onSelect={onSelect}
        editorMode={editorMode}
        onHoverChange={onHoverChange}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      >
        <div className="pointer-events-none">
          <Component settings={ctaSettings} />
        </div>
      </BlockWrapper>
    );
  }
  
  // For data-driven blocks
  return (
    <BlockWrapper
      blockId={block.id}
      blockLabel={label}
      settingsKey={block.settings_key}
      isSelected={isSelected}
      onSelect={onSelect}
      editorMode={editorMode}
      onHoverChange={onHoverChange}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
    >
      <div className="pointer-events-none">
        <Component 
          data={blockData || []}
          settings={mergedProps}
          title={mergedProps.title}
        />
      </div>
    </BlockWrapper>
  );
};

const PagePreviewRenderer = ({ 
  pageId, 
  blocks = [], 
  selectedBlockId, 
  onSelectBlock,
  viewport = 'desktop',
  editorMode,
  onSectionHover,
  onDuplicateSection,
  onDeleteSection,
}) => {
  const [blockDataMap, setBlockDataMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data for all homepage blocks
  useEffect(() => {
    if (pageId !== 'homepage') {
      setLoading(false);
      return;
    }

    const fetchAllBlockData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const dataMap = {};
        
        // Fetch data for each block that needs it
        for (const block of blocks) {
          if (!block.enabled) continue;
          
          const config = HOMEPAGE_BLOCK_CONFIG[block.type];
          if (!config) continue;
          
          // Skip blocks with no data requirements
          if (!config.dataEndpoint && !config.multipleEndpoints) continue;
          
          try {
            // Handle blocks that need multiple endpoints (like trending)
            if (config.multipleEndpoints) {
              const results = {};
              for (const ep of config.multipleEndpoints) {
                const response = await axios.get(`${API}${ep.endpoint}`, { params: ep.params || {} });
                results[ep.key] = response.data || [];
              }
              dataMap[block.id] = results;
            } else {
              // Single endpoint
              const params = config.dataParams || {};
              const response = await axios.get(`${API}${config.dataEndpoint}`, { params });
              
              // Extract data using the configured key
              if (config.dataKey && response.data) {
                const data = response.data[config.dataKey] || [];
                // Transform to trending format if needed
                if (config.transformToTrending) {
                  dataMap[block.id] = { trending: data, popular: [] };
                } else {
                  dataMap[block.id] = data;
                }
              } else {
                dataMap[block.id] = response.data || [];
              }
            }
          } catch (err) {
            console.warn(`Failed to fetch data for block ${block.id}:`, err.message);
            // Provide fallback data based on block type
            if (config.multipleEndpoints) {
              dataMap[block.id] = { trending: [], popular: [] };
            } else {
              dataMap[block.id] = [];
            }
          }
        }
        
        setBlockDataMap(dataMap);
      } catch (err) {
        console.error('Error fetching block data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllBlockData();
  }, [pageId, blocks]);

  // Calculate viewport width
  const getViewportStyle = () => {
    switch (viewport) {
      case 'mobile':
        return { maxWidth: '375px', margin: '0 auto' };
      case 'tablet':
        return { maxWidth: '768px', margin: '0 auto' };
      default:
        return { width: '100%' };
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-black" data-testid="preview-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-2" />
          <p className="text-zinc-400 text-sm">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-black">
        <div className="text-center text-red-400">
          <p>Error loading preview</p>
          <p className="text-sm text-zinc-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // For landing page, we'd use iframe - but this component focuses on direct rendering
  if (pageId === 'landing_early_access') {
    return (
      <div className="h-full flex items-center justify-center bg-black">
        <p className="text-zinc-400">Landing page uses iframe preview</p>
      </div>
    );
  }

  // Render homepage blocks
  const enabledBlocks = blocks
    .filter(b => b.enabled)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div 
      className="min-h-full bg-black overflow-auto"
      style={getViewportStyle()}
      onClick={() => onSelectBlock(null)} // Deselect when clicking background
      data-testid="homepage-preview"
    >
      {enabledBlocks.length === 0 ? (
        <div className="h-full flex items-center justify-center py-20">
          <p className="text-zinc-500">No blocks enabled. Add blocks from the left panel.</p>
        </div>
      ) : (
        <div className="pb-8">
          {enabledBlocks.map((block) => (
            <HomepageBlockRenderer
              key={block.id}
              block={block}
              blockData={blockDataMap[block.id]}
              isSelected={selectedBlockId === block.id}
              onSelect={onSelectBlock}
              editorMode={editorMode}
              onHoverChange={onSectionHover}
              onDuplicate={onDuplicateSection}
              onDelete={onDeleteSection}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PagePreviewRenderer;
